import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import Logo from "../assets/sun.png";

export default function AdminBinaryTreeSearch() {
  const navigate = useNavigate();

  const [q, setQ] = useState("");
  const [searched, setSearched] = useState("");
  const [targetUser, setTargetUser] = useState(null);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const canvasRef = useRef(null);

  const token = useMemo(
    () => localStorage.getItem("token") || localStorage.getItem("authToken") || "",
    []
  );

  useEffect(() => {
    // Check Auth on mount
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const role = String(user?.role || "").toUpperCase();

    if (!token || !(role.includes("ADMIN") || role.includes("STAFF"))) {
      navigate("/login", { replace: true });
    }
  }, [navigate, token]);

  const fetchTree = async (userIdLike) => {
    const query = String(userIdLike || "").trim();
    if (!query) return;

    setErr("");
    setLoading(true);

    try {
      const res = await axiosInstance.get(`/api/binary/admin/tree`, {
        params: { q: query },
        headers: { Authorization: `Bearer ${token}` },
      });

      const resData = res.data;
      setSearched(resData?.searched || query);
      setTargetUser(resData?.targetUser || null);
      setData(resData);
      setQ(query);

      const root = resData?.tree;
      const rid =
        root?.userID ?? root?.userId ?? root?.id ?? root?.userPkId ?? null;
      if (rid != null) setSelectedId(rid);
    } catch (e) {
      console.log("admin tree search error", e);
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        navigate("/admin", { replace: true });
      } else {
        setErr(
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.response?.data?.msg ||
          e?.message ||
          "Failed to load admin tree"
        );
      }
      setTargetUser(null);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const root = data?.tree || null;

  const zoomIn = () =>
    setZoom((z) => Math.min(2.0, Math.round((z + 0.1) * 10) / 10));
  const zoomOut = () =>
    setZoom((z) => Math.max(0.4, Math.round((z - 0.1) * 10) / 10));
  const zoomReset = () => setZoom(1);

  const centerCanvas = () => {
    const el = canvasRef.current;
    if (!el) return;
    el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
    el.scrollTop = 0;
  };

  useEffect(() => {
    if (!loading) setTimeout(centerCanvas, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data, zoom]);

  const onSubmitSearch = (e) => {
    e.preventDefault();
    fetchTree(q);
  };

  return (
    <div className="neo-page">
      <style>{CSS}</style>

      {err ? (
        <div className="neo-error">
          <b>Error:</b> {err}
        </div>
      ) : null}

      <div className="neo-top">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", gap: "10px", flexWrap: "wrap", marginBottom: "8px" }}>
          <h3 style={{ margin: 0, fontSize: "16px" }}>Admin Tree Search</h3>
          <button
            onClick={() => navigate("/admin/dashboard")}
            style={{ padding: "6px 12px", borderRadius: "10px", border: "1px solid #ccc", background: "#fff", cursor: "pointer", fontWeight: "bold" }}>
            ← Dashboard
          </button>
        </div>

        <form onSubmit={onSubmitSearch} style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px", width: "100%" }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Enter UserID (e.g. SUN114033)"
            style={{ padding: "8px 12px", borderRadius: "10px", border: "1px solid #ddd", flex: 1, minWidth: "200px", outline: "none" }}
          />
          <button type="submit" disabled={loading || !q.trim()} style={{ padding: "8px 16px", borderRadius: "10px", border: "none", background: "#0ea5e9", color: "#fff", fontWeight: "bold", cursor: "pointer" }}>
            {loading ? "Searching..." : "Search"}
          </button>
        </form>

        <div className="neo-zoom">
          {/* Zoom controls */}
          <button className="neo-ic" onClick={zoomOut} title="Zoom out">−</button>
          <div className="neo-zoomv">{Math.round(zoom * 100)}%</div>
          <button className="neo-ic" onClick={zoomIn} title="Zoom in">+</button>
          <button className="neo-ic ghost" onClick={zoomReset} title="Reset zoom">R</button>
          <button className="neo-ic ghost" onClick={centerCanvas} title="Center tree">C</button>

          <button
            className="neo-btn"
            onClick={() => fetchTree(searched)}
            disabled={loading || !searched}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>

        {/* API info pill */}
        <div className="neo-api-pill">
          <span className="neo-api-dot" />
          <span className="neo-api-text">
            Search Target: <b>{searched || "None"}</b> {targetUser && `(${targetUser.name})`}
          </span>
        </div>
      </div>

      <div className="neo-canvas" ref={canvasRef}>
        {loading ? (
          <div className="neo-loading">
            <div className="neo-spinner" />
            <div className="neo-loading-text">Loading admin tree…</div>
          </div>
        ) : null}

        {!loading && !root ? (
          <div className="neo-empty">No tree data found. Search a UserID to render their tree.</div>
        ) : null}

        {root ? (
          <div className="neo-stage" style={{ transform: `scale(${zoom})` }}>
            <ul className="neo-tree">
              <NeoNode
                node={root}
                selectedId={selectedId}
                onSelect={(id) => {
                  setSelectedId(id);
                  if (id) {
                    setQ(id);
                    fetchTree(id); // Drill down on click
                  }
                }}
                logoSrc={Logo}
              />
            </ul>
          </div>
        ) : null}
      </div>

      <div className="neo-footer">© Sun entrepreneur — Admin Binary Tree</div>
    </div>
  );
}

/** -------- helpers -------- **/
function getUserType(node) {
  const raw =
    node?.userType ??
    node?.usertype ??
    node?.type ??
    node?.roleType ??
    node?.memberType ??
    "";
  return String(raw || "").trim().toUpperCase();
}

/** -------- NODE -------- **/
function NeoNode({ node, selectedId, onSelect, logoSrc }) {
  const left = node?.left || null;
  const right = node?.right || null;
  const hasChildren = !!left || !!right;

  const uid = node?.userID ?? node?.userId ?? node?.id ?? node?.userPkId ?? "—";
  const name = node?.name ?? "—";
  const userType = getUserType(node);
  const isSelected = String(selectedId) === String(uid);
  const isEntrepreneur = userType === "ENTREPRENEUR";
  const typeClass = isEntrepreneur ? "type-ent" : "type-red";

  return (
    <li className="neo-li">
      <button
        type="button"
        className={["neo-card", isSelected ? "sel" : ""].join(" ")}
        onClick={() => onSelect(uid)}
      >
        <div className="neo-name">{name}</div>
        <img
          className="neo-logo"
          src={logoSrc}
          alt="logo"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <div className="neo-id">{uid}</div>
        <div className={"neo-chip " + typeClass}>{userType || "—"}</div>

        {/* TOOLTIP FOR HOVER */}
        <div className="neo-tooltip">
          <div><b>Sponsor ID:</b> {node?.sponsorPkId || "—"}</div>
          <div><b>Sponsor Name:</b> {node?.sponsorName || "—"}</div>
          <div><b>Joining Date:</b> {node?.joiningDate ? new Date(node.joiningDate).toLocaleDateString() : "—"}</div>
        </div>
      </button>

      {hasChildren ? (
        <ul className="neo-ul">
          <NeoSlot child={left} side="LEFT" selectedId={selectedId} onSelect={onSelect} logoSrc={logoSrc} />
          <NeoSlot child={right} side="RIGHT" selectedId={selectedId} onSelect={onSelect} logoSrc={logoSrc} />
        </ul>
      ) : null}
    </li>
  );
}

function NeoSlot({ child, side, selectedId, onSelect, logoSrc }) {
  if (!child) {
    return (
      <li className="neo-li">
        <div className="neo-card empty" role="presentation">
          <div className="neo-name">Empty</div>
          <div className="neo-side">{side}</div>
          <div className="neo-id">—</div>
          <div className="neo-chip type-red">—</div>
        </div>
      </li>
    );
  }
  return <NeoNode node={child} selectedId={selectedId} onSelect={onSelect} logoSrc={logoSrc} />;
}

/** CSS */
const CSS =
  ".neo-page{min-height:100vh;background:#fff;color:#0f172a;font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Arial;}" +
  ".neo-error{margin:10px 12px 0;border-radius:12px;border:1px solid rgba(239,68,68,.25);background:rgba(239,68,68,.06);padding:10px 12px;color:#991b1b;font-size:13px;}" +

  ".neo-top{position:sticky;top:0;z-index:10;background:rgba(255,255,255,.95);backdrop-filter:blur(10px);border-bottom:1px solid rgba(15,23,42,.10);padding:8px 10px 6px;}" +
  ".neo-zoom{display:flex;align-items:center;gap:6px;flex-wrap:wrap;justify-content:center;}" +
  ".neo-ic{width:30px;height:30px;border-radius:10px;border:1px solid rgba(15,23,42,.12);background:#fff;color:#0f172a;font-weight:950;cursor:pointer;font-size:16px;line-height:1;transition:background .14s;}" +
  ".neo-ic:hover{background:#f1f5f9;}" +
  ".neo-ic.ghost{opacity:.9;font-size:12px;font-weight:800;}" +
  ".neo-zoomv{font-size:11px;font-weight:950;opacity:.9;width:52px;text-align:center;color:#334155;}" +

  ".neo-btn{border:1px solid rgba(14,165,233,.25);cursor:pointer;padding:6px 12px;border-radius:10px;font-weight:800;color:#075985;background:rgba(14,165,233,.10);font-size:12px;transition:background .14s;white-space:nowrap;}" +
  ".neo-btn:hover{background:rgba(14,165,233,.18);}" +
  ".neo-btn:disabled{opacity:.7;cursor:not-allowed;}" +

  /* API pill */
  ".neo-api-pill{display:flex;align-items:center;justify-content:center;gap:6px;margin-top:5px;}" +
  ".neo-api-dot{width:7px;height:7px;border-radius:50%;background:#22c55e;flex-shrink:0;box-shadow:0 0 0 2px rgba(34,197,94,.20);}" +
  ".neo-api-text{font-size:11px;color:#64748b;font-family:ui-monospace,monospace;}" +
  ".neo-api-text b{color:#0ea5e9;}" +

  /* Loading */
  ".neo-loading{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;padding:60px 20px;}" +
  ".neo-spinner{width:36px;height:36px;border-radius:50%;border:3px solid rgba(14,165,233,.15);border-top-color:#0ea5e9;animation:neo-spin .7s linear infinite;}" +
  "@keyframes neo-spin{to{transform:rotate(360deg)}}" +
  ".neo-loading-text{font-size:13px;font-weight:600;color:#64748b;}" +

  /* canvas + stage */
  ".neo-canvas{padding:10px 8px 26px;overflow:auto;-webkit-overflow-scrolling:touch;height:calc(100vh - 148px);}" +
  ".neo-stage{transform-origin:top center;width:100%;display:flex;justify-content:center;}" +

  /* tree */
  ".neo-tree{padding-left:0;margin:0 auto;display:flex;justify-content:center;width:max-content;position:relative;}" +
  ".neo-tree,.neo-tree ul{padding-top:14px;position:relative;}" +
  ".neo-ul{padding-left:0;margin:0 auto;display:flex;justify-content:center;gap:24px;width:100%;}" +
  ".neo-li{list-style:none;position:relative;padding:12px 6px 0 6px;display:flex;flex-direction:column;align-items:center;}" +

  /* lines */
  ".neo-li:before,.neo-li:after{content:'';position:absolute;top:0;right:50%;border-top:2px solid rgba(15,23,42,.10);width:50%;height:14px;}" +
  ".neo-li:after{right:auto;left:50%;border-left:2px solid rgba(15,23,42,.10);}" +
  ".neo-li:only-child:before,.neo-li:only-child:after{display:none;}" +
  ".neo-li:only-child{padding-top:0;}" +
  ".neo-li:first-child:before,.neo-li:last-child:after{border:0 none;}" +
  ".neo-li:last-child:before{border-right:2px solid rgba(15,23,42,.10);border-radius:0 10px 0 0;}" +
  ".neo-li:first-child:after{border-radius:10px 0 0 0;}" +
  ".neo-tree ul:before{content:'';position:absolute;top:0;left:50%;border-left:2px solid rgba(15,23,42,.10);width:0;height:14px;}" +

  /* card */
  ".neo-card{position:relative;width:132px;min-height:112px;border-radius:14px;padding:10px;border:1px solid rgba(15,23,42,.10);background:#fff;box-shadow:0 14px 34px rgba(2,6,23,.08);display:flex;flex-direction:column;align-items:center;gap:6px;cursor:pointer;user-select:none;transition:transform .14s ease,box-shadow .14s ease,border-color .14s ease;}" +
  ".neo-card:hover{transform:translateY(-2px);border-color:rgba(14,165,233,.30);z-index:20;}" +
  ".neo-card.sel{border-color:rgba(99,102,241,.35);box-shadow:0 16px 46px rgba(99,102,241,.12),0 14px 34px rgba(2,6,23,.10);}" +
  ".neo-card.empty{opacity:.55;cursor:default;}" +

  ".neo-name{font-size:13px;font-weight:950;letter-spacing:.2px;color:#0f172a;text-transform:lowercase;line-height:1.1;text-align:center;}" +
  ".neo-logo{width:34px;height:34px;object-fit:contain;border-radius:10px;border:1px solid rgba(15,23,42,.10);background:#fff;padding:5px;}" +
  ".neo-id{font-size:12px;font-weight:950;color:#0ea5e9;line-height:1.1;}" +
  ".neo-chip{font-size:11px;font-weight:950;padding:6px 10px;border-radius:999px;border:1px solid rgba(15,23,42,.10);background:#f8fafc;color:#0f172a;line-height:1;}" +
  ".neo-chip.type-ent{color:#166534;border-color:rgba(34,197,94,.30);background:rgba(34,197,94,.12);}" +
  ".neo-chip.type-red{color:#991b1b;border-color:rgba(239,68,68,.30);background:rgba(239,68,68,.12);}" +
  ".neo-side{font-size:11px;font-weight:900;color:#475569;}" +

  /* tooltip */
  ".neo-tooltip{position:absolute;top:50%;left:100%;transform:translateY(-50%);margin-left:8px;background:#0f172a;color:#f8fafc;padding:8px 12px;border-radius:8px;font-size:11px;font-weight:500;white-space:nowrap;opacity:0;visibility:hidden;transition:opacity .2s,visibility .2s;z-index:99;text-align:left;line-height:1.5;box-shadow:0 10px 15px -3px rgba(0,0,0,0.1),0 4px 6px -4px rgba(0,0,0,0.1);}" +
  ".neo-tooltip::after{content:'';position:absolute;top:50%;right:100%;transform:translateY(-50%);border-width:5px;border-style:solid;border-color:transparent #0f172a transparent transparent;}" +
  ".neo-card:hover:not(.empty) .neo-tooltip{opacity:1;visibility:visible;}" +

  ".neo-empty{margin:16px auto;width:fit-content;padding:10px 12px;border-radius:12px;border:1px dashed rgba(15,23,42,.20);color:#475569;background:#fff;font-size:13px;}" +
  ".neo-footer{padding:10px 10px 12px;text-align:center;color:#64748b;border-top:1px solid rgba(15,23,42,.10);background:rgba(248,250,252,.85);backdrop-filter:blur(10px);font-size:12px;}" +

  /* mobile */
  "@media (max-width:520px){" +
  ".neo-canvas{height:calc(100vh - 170px);}" +
  ".neo-card{width:118px;min-height:106px;border-radius:13px;padding:9px;}" +
  ".neo-ul{gap:10px;}" +
  ".neo-logo{width:30px;height:30px;}" +
  ".neo-name{font-size:11px;}" +
  ".neo-id{font-size:11px;}" +
  ".neo-chip{font-size:10px;padding:4px 8px;}" +
  "}";